import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface MapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch?: number;
  bearing?: number;
}

export interface MapBookmark {
  name: string;
  center: [number, number];
  zoom: number;
}

export interface AIMapMarker {
  id: string;
  latitude: number;
  longitude: number;
  label: string;
  type: 'colocation' | 'dwell' | 'poi' | 'alert' | 'custom';
  color?: string;
  description?: string;
  entities?: string[];
}

/** Org / session Pathfinder track-filter defaults */
export interface PathfinderTrackFilterState {
  hideImplausibleMovement: boolean;
  implausibleMaxSpeedKmh: number;
  hideGaps: boolean;
  gapMaxTimeMinutes: number;
  gapMaxDistanceM: number;
}

export const PATHFINDER_TRACK_FILTER_FALLBACKS: PathfinderTrackFilterState = {
  hideImplausibleMovement: true,
  implausibleMaxSpeedKmh: 160,
  hideGaps: false,
  gapMaxTimeMinutes: 30,
  gapMaxDistanceM: 500,
};

export function clampImplausibleMaxSpeedKmh(kmh: number): number {
  if (!Number.isFinite(kmh)) return PATHFINDER_TRACK_FILTER_FALLBACKS.implausibleMaxSpeedKmh;
  return Math.max(80, Math.min(300, Math.round(kmh)));
}

export function clampGapMaxTimeMinutes(min: number): number {
  if (!Number.isFinite(min)) return PATHFINDER_TRACK_FILTER_FALLBACKS.gapMaxTimeMinutes;
  return Math.max(5, Math.min(180, Math.round(min)));
}

export function clampGapMaxDistanceM(m: number): number {
  if (!Number.isFinite(m)) return PATHFINDER_TRACK_FILTER_FALLBACKS.gapMaxDistanceM;
  return Math.max(100, Math.min(5000, Math.round(m)));
}

// Map bookmarks constant (shared)
export const MAP_BOOKMARKS: Record<string, MapBookmark> = {
  // Auto has no fixed center — COPDashboard computes densest active cluster.
  'Auto': { name: 'Auto', center: [32.2217, -110.9265], zoom: 12 },
  'Tucson': { name: 'Tucson', center: [32.2217, -110.9265], zoom: 12 },
  'Phoenix': { name: 'Phoenix', center: [33.4484, -112.0740], zoom: 11 },
  'Flagstaff': { name: 'Flagstaff', center: [35.1983, -111.6513], zoom: 12 },
  'Arizona': { name: 'Arizona', center: [34.0489, -111.0937], zoom: 6 },
};

interface MapState {
  // View state
  viewState: MapViewState | null;
  mapCenter: [number, number];
  mapZoom: number;
  selectedBookmark: string;
  userHasInteractedWithMap: boolean;
  
  // AI-generated markers from Smart Analytics
  aiMarkers: AIMapMarker[];
  
  // Display options (for Pathfinder)
  showDataPoints: boolean;
  showRouteLines: boolean;
  showDirectionArrows: boolean;
  showHeatmap: boolean;
  showDwells: boolean;
  enableClustering: boolean;
  hideGaps: boolean;
  hideImplausibleMovement: boolean;
  implausibleMaxSpeedKmh: number;
  gapMaxTimeMinutes: number;
  gapMaxDistanceM: number;
  /** True after the user changes any map track-filter control */
  trackFiltersUserCustomized: boolean;
  
  // Co-traveler options
  showCoTravelerPoints: boolean;
  showCoTravelerLines: boolean;
  showCoTravelerHeatmap: boolean;
  showCoTravelerConnections: boolean;
  
  // Other options
  followMode: boolean;
  isDrawingMode: boolean;
  
  // Actions
  setViewState: (viewState: MapViewState | null) => void;
  setMapCenter: (center: [number, number]) => void;
  setMapZoom: (zoom: number) => void;
  setSelectedBookmark: (bookmark: string) => void;
  setUserHasInteractedWithMap: (hasInteracted: boolean) => void;
  setAiMarkers: (markers: AIMapMarker[]) => void;
  addAiMarkers: (markers: AIMapMarker[]) => void;
  clearAiMarkers: () => void;
  setShowDataPoints: (show: boolean) => void;
  setShowRouteLines: (show: boolean) => void;
  setShowDirectionArrows: (show: boolean) => void;
  setShowHeatmap: (show: boolean) => void;
  setShowDwells: (show: boolean) => void;
  setEnableClustering: (enable: boolean) => void;
  setHideGaps: (hide: boolean, options?: { userCustomized?: boolean }) => void;
  setHideImplausibleMovement: (hide: boolean, options?: { userCustomized?: boolean }) => void;
  setImplausibleMaxSpeedKmh: (kmh: number, options?: { userCustomized?: boolean }) => void;
  setGapMaxTimeMinutes: (minutes: number, options?: { userCustomized?: boolean }) => void;
  setGapMaxDistanceM: (meters: number, options?: { userCustomized?: boolean }) => void;
  applyOrgTrackFilters: (filters: PathfinderTrackFilterState) => void;
  markTrackFiltersUserCustomized: () => void;
  setShowCoTravelerPoints: (show: boolean) => void;
  setShowCoTravelerLines: (show: boolean) => void;
  setShowCoTravelerHeatmap: (show: boolean) => void;
  setShowCoTravelerConnections: (show: boolean) => void;
  setFollowMode: (enabled: boolean) => void;
  setIsDrawingMode: (isDrawing: boolean) => void;
  goToBookmark: (bookmarkKey: string) => void;
  resetMapState: () => void;
}

export const useMapStore = create<MapState>()(
  persist(
    (set, _get) => ({
      viewState: null,
      mapCenter: [32.2217, -110.9265], // Tucson default
      mapZoom: 11,
      selectedBookmark: 'Auto',
      userHasInteractedWithMap: false,
      aiMarkers: [],
      showDataPoints: true,
      showRouteLines: true,
      showDirectionArrows: true,
      showHeatmap: false,
      showDwells: true,
      enableClustering: true,
      hideGaps: PATHFINDER_TRACK_FILTER_FALLBACKS.hideGaps,
      hideImplausibleMovement: PATHFINDER_TRACK_FILTER_FALLBACKS.hideImplausibleMovement,
      implausibleMaxSpeedKmh: PATHFINDER_TRACK_FILTER_FALLBACKS.implausibleMaxSpeedKmh,
      gapMaxTimeMinutes: PATHFINDER_TRACK_FILTER_FALLBACKS.gapMaxTimeMinutes,
      gapMaxDistanceM: PATHFINDER_TRACK_FILTER_FALLBACKS.gapMaxDistanceM,
      trackFiltersUserCustomized: false,
      // Co-traveler layers should be OFF by default (avoid clutter)
      showCoTravelerPoints: false,
      showCoTravelerLines: false,
      showCoTravelerHeatmap: false,
      showCoTravelerConnections: false,
      followMode: false,
      isDrawingMode: false,

      setViewState: (viewState: MapViewState | null) => set({ viewState }),
      
      setMapCenter: (center: [number, number]) => set({ mapCenter: center }),
      
      setMapZoom: (zoom: number) => set({ mapZoom: zoom }),
      
      setSelectedBookmark: (bookmark: string) => set({ selectedBookmark: bookmark }),
      
      setUserHasInteractedWithMap: (hasInteracted: boolean) => 
        set({ userHasInteractedWithMap: hasInteracted }),
      
      setAiMarkers: (markers: AIMapMarker[]) => set({ aiMarkers: markers }),
      
      addAiMarkers: (markers: AIMapMarker[]) => 
        set((state) => ({ aiMarkers: [...state.aiMarkers, ...markers] })),
      
      clearAiMarkers: () => set({ aiMarkers: [] }),
      
      setShowDataPoints: (show: boolean) => set({ showDataPoints: show }),
      
      setShowRouteLines: (show: boolean) => set({ showRouteLines: show }),
      
      setShowDirectionArrows: (show: boolean) => set({ showDirectionArrows: show }),
      
      setShowHeatmap: (show: boolean) => set({ showHeatmap: show }),
      
      setShowDwells: (show: boolean) => set({ showDwells: show }),
      
      setEnableClustering: (enable: boolean) => set({ enableClustering: enable }),
      
      setHideGaps: (hide: boolean, options) =>
        set({
          hideGaps: hide,
          ...(options?.userCustomized === false ? {} : { trackFiltersUserCustomized: true }),
        }),

      setHideImplausibleMovement: (hide: boolean, options) =>
        set({
          hideImplausibleMovement: hide,
          ...(options?.userCustomized === false ? {} : { trackFiltersUserCustomized: true }),
        }),

      setImplausibleMaxSpeedKmh: (kmh: number, options) =>
        set({
          implausibleMaxSpeedKmh: clampImplausibleMaxSpeedKmh(kmh),
          ...(options?.userCustomized === false ? {} : { trackFiltersUserCustomized: true }),
        }),

      setGapMaxTimeMinutes: (minutes: number, options) =>
        set({
          gapMaxTimeMinutes: clampGapMaxTimeMinutes(minutes),
          ...(options?.userCustomized === false ? {} : { trackFiltersUserCustomized: true }),
        }),

      setGapMaxDistanceM: (meters: number, options) =>
        set({
          gapMaxDistanceM: clampGapMaxDistanceM(meters),
          ...(options?.userCustomized === false ? {} : { trackFiltersUserCustomized: true }),
        }),

      applyOrgTrackFilters: (filters: PathfinderTrackFilterState) =>
        set({
          hideImplausibleMovement: filters.hideImplausibleMovement,
          implausibleMaxSpeedKmh: clampImplausibleMaxSpeedKmh(filters.implausibleMaxSpeedKmh),
          hideGaps: filters.hideGaps,
          gapMaxTimeMinutes: clampGapMaxTimeMinutes(filters.gapMaxTimeMinutes),
          gapMaxDistanceM: clampGapMaxDistanceM(filters.gapMaxDistanceM),
          trackFiltersUserCustomized: false,
        }),

      markTrackFiltersUserCustomized: () => set({ trackFiltersUserCustomized: true }),
      
      setShowCoTravelerPoints: (show: boolean) => set({ showCoTravelerPoints: show }),
      
      setShowCoTravelerLines: (show: boolean) => set({ showCoTravelerLines: show }),
      
      setShowCoTravelerHeatmap: (show: boolean) => set({ showCoTravelerHeatmap: show }),
      
      setShowCoTravelerConnections: (show: boolean) => set({ showCoTravelerConnections: show }),
      
      setFollowMode: (enabled: boolean) => set({ followMode: enabled }),
      
      setIsDrawingMode: (isDrawing: boolean) => set({ isDrawingMode: isDrawing }),
      
      goToBookmark: (bookmarkKey: string) => {
        const bookmark = MAP_BOOKMARKS[bookmarkKey];
        if (bookmark && bookmarkKey !== 'Auto') {
          set({
            selectedBookmark: bookmarkKey,
            mapCenter: bookmark.center,
            mapZoom: bookmark.zoom,
            viewState: {
              latitude: bookmark.center[0],
              longitude: bookmark.center[1],
              zoom: bookmark.zoom,
            },
            userHasInteractedWithMap: false,
          });
        } else {
          // Re-enable densest-active-cluster auto-zoom.
          set({
            selectedBookmark: 'Auto',
            viewState: null,
            userHasInteractedWithMap: false,
          });
        }
      },
      
      resetMapState: () =>
        set({
          viewState: null,
          mapCenter: [32.2217, -110.9265],
          mapZoom: 11,
          selectedBookmark: 'Auto',
          userHasInteractedWithMap: false,
          // Reset co-traveler layers to off
          showCoTravelerPoints: false,
          showCoTravelerLines: false,
          showCoTravelerHeatmap: false,
          showCoTravelerConnections: false,
        }),
    }),
    {
      name: 'map-storage',
      version: 3,
      migrate: (persistedState: unknown, version) => {
        const state = (persistedState ?? {}) as Record<string, any>;
        let next = { ...state };
        if (version < 2) {
          next = {
            ...next,
            showCoTravelerPoints: false,
            showCoTravelerLines: false,
            showCoTravelerHeatmap: false,
            showCoTravelerConnections: false,
          };
        }
        if (version < 3) {
          next = {
            ...next,
            hideImplausibleMovement:
              next.hideImplausibleMovement ?? PATHFINDER_TRACK_FILTER_FALLBACKS.hideImplausibleMovement,
            implausibleMaxSpeedKmh: clampImplausibleMaxSpeedKmh(
              next.implausibleMaxSpeedKmh ?? PATHFINDER_TRACK_FILTER_FALLBACKS.implausibleMaxSpeedKmh,
            ),
            gapMaxTimeMinutes: clampGapMaxTimeMinutes(
              next.gapMaxTimeMinutes ?? PATHFINDER_TRACK_FILTER_FALLBACKS.gapMaxTimeMinutes,
            ),
            gapMaxDistanceM: clampGapMaxDistanceM(
              next.gapMaxDistanceM ?? PATHFINDER_TRACK_FILTER_FALLBACKS.gapMaxDistanceM,
            ),
            // Existing users only had hideGaps; treat as not customized so
            // org defaults for the new implausible filter can seed once.
            trackFiltersUserCustomized: next.trackFiltersUserCustomized ?? false,
          };
        }
        return next;
      },
      partialize: (state: MapState) => ({
        selectedBookmark: state.selectedBookmark,
        // Remember where the user left the map instead of snapping back to the
        // Tucson default on every visit; loading data still re-fits to the tracks.
        mapCenter: state.mapCenter,
        mapZoom: state.mapZoom,
        showDataPoints: state.showDataPoints,
        showRouteLines: state.showRouteLines,
        showDirectionArrows: state.showDirectionArrows,
        showHeatmap: state.showHeatmap,
        showDwells: state.showDwells,
        enableClustering: state.enableClustering,
        hideGaps: state.hideGaps,
        hideImplausibleMovement: state.hideImplausibleMovement,
        implausibleMaxSpeedKmh: state.implausibleMaxSpeedKmh,
        gapMaxTimeMinutes: state.gapMaxTimeMinutes,
        gapMaxDistanceM: state.gapMaxDistanceM,
        trackFiltersUserCustomized: state.trackFiltersUserCustomized,
        showCoTravelerPoints: state.showCoTravelerPoints,
        showCoTravelerLines: state.showCoTravelerLines,
        showCoTravelerHeatmap: state.showCoTravelerHeatmap,
        showCoTravelerConnections: state.showCoTravelerConnections,
      }),
    }
  )
);
